<!--
  The performance dashboard: one scope (day, week or month), the headline figures, then a
  card per project. A margin is never shown without the unpriced count beside
  it — a figure that silently omits lines is worse than no figure.

  Look: the quality area's site list with money in it — a navy hero carrying
  the totals and the way through to the other areas, then project cards with
  a safety-orange rail.
-->
<script lang="ts">
  import { api } from '../../lib/api';
  import { money } from '../../lib/money';
  import { linkHandler, location, routePath } from '../../lib/router';
  import { notifyError } from '../../lib/store';
  import type { PnlDashboardResponse } from '../../lib/types';
  import AdminHero from './AdminHero.svelte';
  import ScopePicker from './ScopePicker.svelte';
  import { readScope, scopeLabel } from './scope';
  import './admin-look.css';

  let data = $state<PnlDashboardResponse | null>(null);
  let loading = $state(true);

  /**
   * How many names on a Daily Report have no rate.
   *
   * Deliberately NOT month-scoped, unlike unpriced_count below it: a name
   * with no dollar amount is setup nobody has finished, not a figure that
   * belongs to the month on screen. Loaded once, outside the scope effect.
   */
  let rateGapCount = $state(0);

  $effect(() => {
    api
      .listRateGaps()
      .then((gaps) => {
        rateGapCount = gaps.workers.length + gaps.materials.length;
      })
      .catch(() => {
        // A hint that cannot load simply does not appear. It must never take
        // the dashboard down with it, and it has nothing to say on failure.
        rateGapCount = 0;
      });
  });

  const scope = $derived(readScope($location.query));
  /** 'day' | 'week' | 'month', for labels. */
  const scopeKind = $derived(scope.month ? 'month' : scope.week ? 'week' : 'day');
  /** The month this scope sits in, for the Expenses link. A week straddling a month end belongs, for that link, to the month its Monday is in. */
  const scopeMonth = $derived.by(() => {
    if (scope.month) return scope.month;
    const anchor = scope.week ?? scope.day;
    return anchor ? anchor.slice(0, 7) : undefined;
  });

  $effect(() => {
    const current = scope;
    loading = true;
    api
      .getPnl(current)
      .then((response) => {
        data = response;
      })
      .catch((cause) => notifyError(cause, 'Could not load the dashboard'))
      .finally(() => {
        loading = false;
      });
  });

  /**
   * Profit is the dollars, margin is the percent — the two are never given
   * the same word. An em dash rather than 0% when there was no revenue to be
   * a percent of, because 0% would read as breaking even.
   */
  function percent(value: number | null | undefined): string {
    return value === null || value === undefined || !Number.isFinite(value) ? '—' : `${value.toFixed(1)}%`;
  }

  const stats = $derived.by(() => {
    if (!data) return undefined;
    const total = data.total;
    // Four figures, in both scopes. Overheads and net profit are NOT tiles
    // here: measured at 800px, six figures need 728px of a 701px strip and
    // the last one spills out of the hero. They get the band below instead,
    // which is where net profit belongs anyway — it is the figure the owner
    // came for, not the sixth of six.
    return [
      { value: money(total.revenue), label: 'revenue' },
      { value: money(total.cost), label: 'cost' },
      {
        value: money(total.margin),
        label: 'profit',
        tone: total.margin < 0 ? ('bad' as const) : ('plain' as const)
      },
      {
        value: percent(total.margin_pct),
        label: 'margin',
        tone: (total.margin_pct ?? 0) < 0 ? ('bad' as const) : ('plain' as const)
      }
    ];
  });
</script>

<AdminHero kicker="Admin" title="Performance Dashboard" {stats}>
  {#snippet action()}
    <!--
      The door to the actual-dollars view, not a summary of it: no figure on
      this pill, because the moment a number from this Daily-Report view sits
      beside a number from the statement view, someone subtracts them.
    -->
    <a class="a-pill" href={routePath.adminBreakdown()} onclick={linkHandler}>Total Breakdown →</a>
  {/snippet}
  {#snippet links()}
    <a class="a-chip" href={routePath.adminSites()} onclick={linkHandler}>Sites</a>
    <a class="a-chip" href={routePath.adminRates()} onclick={linkHandler}>
      Rates
      {#if rateGapCount > 0}
        <span
          class="pill pill--warn"
          title="{rateGapCount} name{rateGapCount === 1 ? '' : 's'} on a Daily Report with no rate entered"
        >!</span>
      {/if}
    </a>
    <a class="a-chip" href={routePath.adminExpenses(scopeMonth)} onclick={linkHandler}>Expenses</a>
    <a class="a-chip" href={routePath.mixes()} onclick={linkHandler}>Mix designs</a>
    <a class="a-chip" href={routePath.quality()} onclick={linkHandler}>Quality</a>
  {/snippet}
</AdminHero>

<div class="scopebar">
  <ScopePicker pathFor={(s) => routePath.admin(s)} />
  <span class="scopebar__label">{scopeKind === 'month' ? 'Month' : scopeKind === 'week' ? 'Week' : 'Day'} · {scopeLabel(scope)}</span>
</div>

{#if loading && data === null}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if data}
  <!--
    Net profit is the figure the owner came for, so it is the largest one on
    the screen rather than a sixth tile in the hero strip: at 380px six tiles
    wrap to two rows of three and this would land bottom-right, which is the
    weakest place a headline figure can sit.
  -->
  {#if data.net_profit !== null && data.overheads !== null}
    <section class="net" aria-labelledby="net-title">
      <div class="net__lead">
        <h2 class="net__kicker" id="net-title">Expected net · this {scopeKind}</h2>
        <p class="net__value a-num" class:is-neg={data.net_profit < 0}>{money(data.net_profit)}</p>
        <p class="net__how">
          {money(data.total.margin)} concrete profit less {money(data.overheads)} of standing monthly
          costs{scopeKind === 'month' ? '' : ` — this ${scopeKind}'s calendar share of them`} — the
          figures you set yourself. What actually moved through the bank is in
          <a href={routePath.adminBreakdown()} onclick={linkHandler}>Total Breakdown</a>.
          {#if data.net_margin_pct !== null}· <span class="a-num">{percent(data.net_margin_pct)}</span> of revenue{/if}
        </p>
      </div>
      <!-- The obvious next question — what is that made of — is this tap. -->
      <a class="net__oh" href={routePath.adminExpenses(scopeMonth)} onclick={linkHandler}>
        <span class="net__oh-label">
          Less standing monthly costs{scopeKind === 'month' ? '' : ` · this ${scopeKind}'s share`}
        </span>
        <span class="net__oh-value a-num">{money(data.overheads)}</span>
        <span class="net__oh-more">
          See what's in it
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
        </span>
      </a>
    </section>
  {/if}

  <!--
    `hired_in` appears only when there is one. A contract job that hires a
    machine or a subcontractor in is the exception, not the rule, and a
    permanent "hired in $0.00" on every ordinary month is noise. It used to be
    added to wages, which told the owner a rented Pump was payroll.
  -->
  <p class="note">
    Cost includes wages <span class="a-num">{money(data.total.labour)}</span>, materials
    <span class="a-num">{money(data.total.materials)}</span>,{#if data.total.hired_in !== 0}
      hired in <span class="a-num">{money(data.total.hired_in)}</span>,{/if} trucking
    <span class="a-num">{money(data.total.trucking)}</span> and PST
    <span class="a-num">{money(data.total.pst_cost)}</span>.
  </p>
  {#if data.total.unpriced_count > 0}
    <p class="flags">
      <span class="pill pill--warn">
        {data.total.unpriced_count} line{data.total.unpriced_count === 1 ? '' : 's'} unpriced — not in these figures
      </span>
    </p>
  {/if}

  <!--
    GST is recoverable, so it is in neither the revenue nor the cost above.
    It is shown here because the difference is what gets remitted — a sunken
    aside rather than a card, so it can never be read as part of the margin.
  -->
  <section class="gst" aria-labelledby="gst-title">
    <h2 class="gst__title" id="gst-title">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 8v8m-3-5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3h3a1.5 1.5 0 0 0 1.5-1.5" /><circle cx="12" cy="12" r="9" /></svg>
      GST
      <span>recoverable — in neither figure above; paid includes the GST on overheads</span>
    </h2>
    <dl class="gst__figs">
      <div><dt>Collected</dt><dd class="a-num">{money(data.total.gst_collected)}</dd></div>
      <div><dt>Paid</dt><dd class="a-num">{money(data.total.gst_paid)}</dd></div>
      <div><dt>Net owing</dt><dd class="a-num">{money(data.total.gst_collected - data.total.gst_paid)}</dd></div>
    </dl>
  </section>

  {#if data.projects.length === 0}
    <div class="empty">
      <strong>Nothing on the books</strong>
      <span class="meta">No daily reports for this {scopeKind}.</span>
    </div>
  {:else}
    <ul class="a-list">
      {#each data.projects as project, i (project.site_id === null ? `text:${project.project}` : `site:${project.site_id}`)}
        <li style="--i: {i}">
          <a
            class="a-card job"
            href={routePath.adminProject(project.project, scope, project.site_id ?? undefined)}
            onclick={linkHandler}
          >
            <span class="a-card__rail" aria-hidden="true"></span>
            <span class="job__body">
              <span class="job__head">
                <span class="job__name">{project.project}</span>
                <span class="job__reports">
                  {project.report_count} report{project.report_count === 1 ? '' : 's'}
                </span>
              </span>
              {#if project.site_id === null}
                <!-- Typed job text nobody has linked to a site: it still prices, it
                     just cannot be renamed or reported on as a site. -->
                <span class="job__unlinked">not linked to a site</span>
              {/if}
              <span class="figs">
                <span class="fig"><small>Revenue</small><b class="a-num">{money(project.revenue)}</b></span>
                <span class="fig"><small>Cost</small><b class="a-num">{money(project.cost)}</b></span>
                <span class="fig">
                  <small>Profit</small>
                  <b class="a-num" class:is-neg={project.margin < 0}>
                    {money(project.margin)}
                    <!-- A profit is never shown without what it leaves out. -->
                    {#if project.unpriced_count > 0}
                      <span class="pill pill--warn" title="Lines that could not be priced are not in these figures">
                        {project.unpriced_count} unpriced
                      </span>
                    {/if}
                  </b>
                </span>
                <span class="fig">
                  <small>Margin</small>
                  <b class="a-num" class:is-neg={(project.margin_pct ?? 0) < 0}>{percent(project.margin_pct)}</b>
                </span>
              </span>
            </span>
            <svg class="a-card__chev" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
          </a>
        </li>
      {/each}
    </ul>
  {/if}
{/if}

<style>
  .scopebar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem 0.8rem;
    padding: 0.6rem 0.75rem;
    margin-bottom: 0.9rem;
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
  }
  .scopebar__label {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
    font-variant-numeric: tabular-nums;
  }

  .note {
    margin: 0 0 0.6rem;
    font-size: 0.84rem;
    color: var(--ink-soft);
  }

  /* ---- net profit ------------------------------------------------------- */
  .net {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem 1rem;
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    border-left: 5px solid var(--accent);
    padding: 0.85rem 1rem;
    margin-bottom: 0.9rem;
  }
  .net__lead { flex: 1 1 11rem; min-width: 0; }
  .net__kicker {
    margin: 0;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--ink-faint);
  }
  .net__value {
    margin: 0.1rem 0 0.15rem;
    /* Deliberately larger than any figure in the hero: this is the answer. */
    font-size: clamp(1.9rem, 9vw, 2.5rem);
    font-weight: 700;
    letter-spacing: -0.03em;
    line-height: 1.05;
  }
  /* Amber, not the error red: a loss is a figure, not a fault. */
  .net__value.is-neg { color: var(--warn); }
  .net__how {
    margin: 0;
    font-size: 0.8rem;
    color: var(--ink-soft);
  }
  .net__how a {
    font-weight: 700;
    color: var(--brand);
  }

  .net__oh {
    flex: 1 1 9rem;
    display: grid;
    align-content: center;
    gap: 0.1rem;
    min-height: var(--tap);
    padding: 0.45rem 0.7rem;
    border-radius: var(--radius);
    background: var(--bg-sunken);
    color: inherit;
    text-decoration: none;
    touch-action: manipulation;
  }
  .net__oh:hover { background: var(--brand-tint); }
  .net__oh:focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; }
  .net__oh-label {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }
  .net__oh-value {
    font-size: 1.15rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
  .net__oh-more {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    font-size: 0.76rem;
    font-weight: 700;
    color: var(--brand);
  }
  .net__oh-more svg {
    width: 0.9rem;
    height: 0.9rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.4;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .flags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin: 0 0 0.9rem;
  }

  /* Sunken and quiet on purpose: GST is neither revenue nor cost, and must
     not sit on a card that looks like the ones carrying margin. */
  .gst {
    background: var(--bg-sunken);
    border: 1px solid var(--line);
    border-radius: var(--radius-lg);
    padding: 0.7rem 0.85rem 0.8rem;
    margin-bottom: 0.9rem;
  }
  .gst__title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin: 0 0 0.5rem;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-soft);
  }
  .gst__title svg {
    width: 1.05rem;
    height: 1.05rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .gst__title span {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
    font-size: 0.8rem;
    color: var(--ink-faint);
  }
  .gst__figs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(7rem, 1fr));
    gap: 0.4rem 0.9rem;
    margin: 0;
  }
  .gst__figs dt {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }
  .gst__figs dd {
    margin: 0;
    font-size: 0.98rem;
    font-weight: 700;
  }

  /* ---- project cards ---------------------------------------------------- */
  .job {
    align-items: stretch;
  }
  .job__body {
    display: grid;
    gap: 0.4rem;
    min-width: 0;
    padding: 0.1rem 0;
  }
  .job__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.6rem;
  }
  .job__name {
    font-size: 1.15rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    overflow-wrap: anywhere;
  }
  .job:hover .job__name {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .job__reports {
    flex: 0 0 auto;
    font-size: 0.78rem;
    color: var(--ink-faint);
    font-variant-numeric: tabular-nums;
  }
  .job__unlinked {
    font-size: 0.78rem;
    color: var(--ink-faint);
  }

  .figs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(7rem, 1fr));
    gap: 0.4rem 0.9rem;
    padding-top: 0.45rem;
    border-top: 1px solid var(--line-soft);
  }
  .fig {
    display: grid;
    min-width: 0;
  }
  .fig small {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }
  .fig b {
    font-size: 1.02rem;
    letter-spacing: -0.01em;
  }
  /* Amber rather than the error red: a loss is a figure the admin came here
     to read, not something that has gone wrong. */
  .fig b.is-neg {
    color: var(--warn);
  }
  .fig .pill {
    margin-left: 0.3rem;
  }

  .empty {
    display: grid;
    gap: 0.4rem;
    justify-items: start;
    background: var(--paper);
    border-radius: var(--radius-lg);
    padding: 1.2rem;
    box-shadow: var(--shadow);
  }
</style>
